import { Grid } from "@mui/material";

import { ProcessExecution } from "../../../model/ProcessExecution";

import { CommonActivityLogGrid } from "../../activity-log";
import { ProcessExecutionAPI } from "../../../api";
import { useNavigate } from "react-router";
import { useAlert } from "../../../lib/alert";
import { OrganizationAccountsProvider } from "../../../providers";

import { AccountProcessExecutions, AccountProcessHero } from ".";
import AccountProcessCard from "./cards/AccountProcessCard";
import ClientContacts from "./ClientContacts";
import TeamMembers from "./TeamMembers";
import {
  AccountPageContext,
  AccountProfileViewer,
} from "../AccountProfilePage";
import { useContext } from "react";
import useFeature from "../../../hooks/useFeature";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import useLinks from "../../../hooks/useLinks";

const AccountProfileOverview = ({
  viewer,
}: {
  viewer: AccountProfileViewer;
}) => {
  const links = useLinks();
  const showAccountProcessCard = useFeature("account.process.card", true);
  const showAccountProcessHero = useFeature("account.process.hero", false);
  const navigate = useNavigate();
  const { handleRejectionWithWarning } = useAlert();
  const { accountListIsMemberOf } = useGlobalOrganizationContext();

  const { account, organization, accountTypeProcesses } =
    useContext(AccountPageContext);
  const accountProcessCardVisible =
    showAccountProcessCard && accountTypeProcesses.length > 0;
  const isAccountUser = accountListIsMemberOf.find(
    (acct) => acct.id === account.id,
  );
  const activityLogCardVisible = !isAccountUser;

  return (
    <>
      <Grid>
        <Grid container spacing={2} sx={{ px: 2 }} alignItems="stretch">
          {showAccountProcessHero && account.process_execution_id && (
            <Grid size={12}>
              <AccountProcessHero
                processExecutionId={account.process_execution_id}
              />
            </Grid>
          )}
          <Grid size={accountProcessCardVisible ? 8 : 12}>
            <AccountProcessExecutions
              organizationId={organization.id}
              subjectId={account.id}
              viewer={viewer}
            />
          </Grid>
          {accountProcessCardVisible && (
            <Grid sx={{ maxHeight: "377px" }} size={4}>
              <AccountProcessCard
                processes={accountTypeProcesses}
                onProcessClick={(process) => {
                  if (process.meta) {
                    ProcessExecutionAPI.start(process.meta, {
                      name: account.account_name
                        ? `${account.account_name} - ${process.name}`
                        : process.name,
                      subject_id: account.id,
                      parameters: {
                        account: account.id,
                      },
                    }).then(
                      (processExecution: ProcessExecution) => {
                        navigate(
                          links.EXECUTION.linkTo(
                            processExecution.process_id,
                            processExecution.id,
                          ),
                        );
                      },
                      (err) => {
                        handleRejectionWithWarning(
                          `Could not start process execution for process ${process.id}`,
                        )(err);
                      },
                    );
                  }
                }}
              />
            </Grid>
          )}
          <Grid size={6}>
            <ClientContacts
              organization={organization}
              accountId={account.id}
            />
          </Grid>
          <Grid size={6}>
            <TeamMembers organization={organization} accountId={account.id} />
          </Grid>
          {activityLogCardVisible && (
            <Grid size={12}>
              <CommonActivityLogGrid
                audits={OrganizationAccountsProvider}
                organizationId={organization.id}
                subjectId={account.id}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default AccountProfileOverview;
